import React, { useState, useEffect, useCallback } from 'react';
import { invCategoryService } from '../../services/inventoryService';
import { card, pageWrap, btn, inputStyle, label, fieldGroup, modal, modalBox,
  sectionTitle, errorBox, emptyState, friendlyErr } from './inventoryStyles';

const Categories = ({ restaurant }) => {
  const [cats, setCats]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await invCategoryService.getAll(restaurantId);
    setCats(data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setName(''); setError(''); setShowForm(true); };
  const openEdit = (cat) => { setEditing(cat); setName(cat.name); setError(''); setShowForm(true); };

  const save = async () => {
    if (!name.trim()) { setError('Category name is required.'); return; }
    setSaving(true); setError('');
    const { error: e } = await invCategoryService.upsert(restaurantId, {
      id: editing?.id,
      name: name.trim(),
      display_order: editing?.display_order ?? cats.length,
      is_active: editing?.is_active ?? true,
    });
    setSaving(false);
    if (e) { setError(friendlyErr(e)); return; }
    setShowForm(false); load();
  };

  const toggleActive = async (cat) => {
    await invCategoryService.setActive(cat.id, !cat.is_active);
    load();
  };

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Categories</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>Group stock items for easier filtering</p>
          </div>
          <button style={btn('primary')} onClick={openNew}>+ Add Category</button>
        </div>

        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={emptyState}>Loading…</div>
          ) : cats.length === 0 ? (
            <div style={emptyState}>
              <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No categories yet</div>
              <div style={{ color: '#aaa', fontSize: '0.88rem' }}>Add categories like Vegetables, Dairy, Packaging to organise your stock items.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                  {['Category Name', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cats.map(cat => (
                  <tr key={cat.id} style={{ borderBottom: '1px solid #f3f4f6', opacity: cat.is_active ? 1 : 0.5 }}>
                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#333' }}>{cat.name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                        background: cat.is_active ? '#f0fdf4' : '#f3f4f6',
                        color: cat.is_active ? '#16a34a' : '#888',
                        border: `1px solid ${cat.is_active ? '#bbf7d0' : '#e5e7eb'}`,
                      }}>
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={{ ...btn('outline'), padding: '5px 12px', fontSize: '0.82rem' }} onClick={() => openEdit(cat)}>Edit</button>
                        <button style={{ ...btn(cat.is_active ? 'danger' : 'green'), padding: '5px 12px', fontSize: '0.82rem' }} onClick={() => toggleActive(cat)}>
                          {cat.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>{editing ? 'Edit Category' : 'New Category'}</h3>
            {error && <div style={errorBox}>{error}</div>}
            <div style={fieldGroup}>
              <label style={label}>Category Name *</label>
              <input
                style={inputStyle(!name.trim())}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Vegetables, Dairy, Packaging…"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && save()}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
